import { cn, ModeToggle } from "@devhop/ui";
import { useLocation } from "@tanstack/react-router";
import { Clock, Monitor, Store } from "lucide-react";
import { useEffect, useState } from "react";
import { ProfileDropdown } from "@/modules/auth/presentation/ui/ProfileDropdown";
import { usePOSContext } from "@/modules/pos/presentation/context/POSContext";
import { OfflineSyncStatusBadge } from "@/modules/pos/presentation/ui/OfflineSyncStatusBadge";
import { ReferenceCacheStatusBadge } from "@/modules/pos/presentation/ui/ReferenceCacheStatusBadge";
import { ShiftStatusIndicator } from "@/modules/pos/presentation/ui/ShiftStatusIndicator";
import {
  formatDateLongLao,
  formatTime24WithSeconds,
} from "@/shared/lib/date-time";

type POSHeaderProps = {
  className?: string;
};

function getPageTitle(pathname: string): string {
  if (pathname.startsWith("/app/pos/orders")) return "ລາຍການ Order";
  if (pathname.startsWith("/app/pos/kitchen")) return "ຄິວຄົວ (KDS)";
  if (pathname === "/app/pos/shifts") return "ລາຍການກະ";
  if (pathname === "/app/pos/settings/bill") return "ຕັ້ງຄ່າບິນ";
  if (pathname === "/app/pos/settings/customer-display") return "ຕັ້ງຄ່າຫນ້າຈໍລູກຄ້າ";
  if (pathname === "/app/pos/settings/sync") return "Offline Sync";
  return "ໜ້າຂາຍ";
}

export function POSHeader({ className }: POSHeaderProps) {
  const { selection } = usePOSContext();
  const { pathname } = useLocation();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const pageTitle = getPageTitle(pathname);

  return (
    <header
      className={cn(
        "z-40 flex h-14 shrink-0 items-center justify-between gap-2 border-b bg-card/95 px-3 backdrop-blur supports-backdrop-filter:bg-card/60 lg:h-16 lg:gap-3 lg:px-4",
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex min-w-0 flex-col">
          <h1 className="truncate font-heading font-semibold text-lg leading-tight">
            {pageTitle}
          </h1>
          {selection && (
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <span className="flex items-center gap-1">
                <Store className="h-3 w-3" />
                <span className="truncate">{selection.branchName}</span>
              </span>
              {selection.deviceName && (
                <span className="hidden items-center gap-1 sm:flex">
                  <Monitor className="h-3 w-3" />
                  <span className="truncate">{selection.deviceName}</span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3">
        <div className="hidden flex-col items-end leading-tight md:flex">
          <span className="flex items-center gap-1.5 font-medium text-sm tabular-nums">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            {formatTime24WithSeconds(currentTime)}
          </span>
          <span className="text-muted-foreground text-xs">
            {formatDateLongLao(currentTime)}
          </span>
        </div>

        {selection && (
          <div className="hidden sm:block lg:hidden">
            <ShiftStatusIndicator />
          </div>
        )}

        {selection && (
          <div className="hidden lg:block">
            <ShiftStatusIndicator />
          </div>
        )}

        <div className="flex items-center gap-1.5 lg:gap-2">
          <ReferenceCacheStatusBadge />
          <OfflineSyncStatusBadge />
        </div>

        <div className="hidden lg:block">
          <ModeToggle />
        </div>
        <ProfileDropdown />
      </div>
    </header>
  );
}
